import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { trpc } from '@duke-hq/svc-support-web/trpc';
import type { ChatEvent } from '@duke-hq/svc-support/trpc/clients/web';
import { ChatContext } from './Context';

// see
// https://github.com/trpc/examples-next-prisma-websockets-starter/blob/main/src/server/routers/post.ts

type Cursor = { pk: string; sk: string };
// `pagedBack` once an older page has loaded: the cursor then belongs to that
// page, and a refetched newest page must not move it back to the start
type Loaded = {
  chatId: string;
  events: Map<string, ChatEvent>;
  cursor?: Cursor;
  pagedBack?: boolean;
};
type Page = { next: Cursor | undefined; older: boolean };

const NO_EVENTS = new Map<string, ChatEvent>();

// Reads through the websocket client, so it needs a `ChatProvider` above it.
// Switching `chatId` starts the new chat from empty without remounting, so a
// host can keep one instance mounted while the student moves between chats.
export function useChatEvents({
  chatId,
  currentUserDrn,
  onMessageReceived,
  enabled = true,
}: {
  chatId: string;
  currentUserDrn: string;
  onMessageReceived?: () => void;
  enabled?: boolean;
}) {
  const ctx = useContext(ChatContext);
  // Held in a ref so the subscription's onData doesn't depend on the callback's
  // identity (which would otherwise re-subscribe on every render).
  const onMessageReceivedRef = useRef(onMessageReceived);
  onMessageReceivedRef.current = onMessageReceived;
  const [loaded, setLoaded] = useState<Loaded>({ chatId, events: new Map() });
  const isLoadingEarlier = useRef(false);
  const utils = trpc.useUtils();
  const openChatId = useRef(chatId);
  openChatId.current = chatId;

  const addEvents = useCallback((forChat: string, events: ChatEvent[], page?: Page) => {
    // A late event or page for the chat just left would otherwise replace this one
    if (forChat !== openChatId.current) {
      return;
    }
    setLoaded((current) => {
      const base: Loaded =
        current.chatId === forChat ? current : { chatId: forChat, events: new Map() };
      const next = new Map(base.events);
      events.forEach((evt) => next.set(evt.id, evt));
      const keepCursor = page === undefined || (!page.older && base.pagedBack === true);
      return {
        chatId: forChat,
        events: next,
        cursor: keepCursor ? base.cursor : page.next,
        pagedBack: base.pagedBack === true || page?.older === true,
      };
    });
  }, []);

  const {
    data: initialPage,
    isError,
    refetch,
  } = trpc.chat.getEvents.useQuery({ chatId, direction: 'reverse-chronological' }, { enabled });

  useEffect(() => {
    if (initialPage?.events === undefined) {
      return;
    }
    addEvents(initialPage.chatId, initialPage.events, { next: initialPage.cursor, older: false });
  }, [initialPage, addEvents]);

  // The server ends every subscription when it drains for a deploy. Without a
  // new one the chat stops receiving messages, and the lazy client closes the
  // socket instead of reconnecting to the next server.
  const [serverEnded, setServerEnded] = useState(false);
  const { reset: resubscribe } = trpc.events.watchEvents.useSubscription(
    {
      chatId: chatId,
      userDrn: currentUserDrn,
    },
    {
      enabled,
      onComplete() {
        setServerEnded(true);
      },
      async onData(e?: { id: string; data: ChatEvent }) {
        const evt = e?.data;
        if (!evt) {
          return;
        }

        switch (evt.ty) {
          case 'user.isTyping':
            return;

          case 'EventUserJoined':
          case 'EventChatClosed':
          case 'EventChatOpened':
          case 'EventUserLeft':
            await ctx.refetchChatMeta(evt.chatId);
            break;
          case 'EventMessage':
            if (evt.userDrn !== currentUserDrn) {
              onMessageReceivedRef.current?.();
            }
            break;
          default:
        }

        addEvents(evt.chatId, [evt]);
      },
      onError(err) {
        console.error('Subscription error:', err);
      },
    },
  );

  useEffect(() => {
    if (!serverEnded) {
      return;
    }
    setServerEnded(false);
    if (enabled) {
      resubscribe();
      void refetch();
    }
  }, [serverEnded, enabled, resubscribe, refetch]);

  const current = loaded.chatId === chatId ? loaded : undefined;
  const cursor = current?.cursor;

  const loadEarlier = useCallback(async () => {
    if (cursor === undefined || isLoadingEarlier.current) {
      return;
    }

    isLoadingEarlier.current = true;

    try {
      const page = await utils.chat.getEvents.fetch({
        chatId,
        cursor,
        direction: 'reverse-chronological',
      });
      addEvents(chatId, page.events, { next: page.cursor, older: true });
    } finally {
      isLoadingEarlier.current = false;
    }
  }, [chatId, cursor, utils, addEvents]);

  const events = current?.events ?? NO_EVENTS;
  const eventsInOrder = useMemo(
    () => Array.from(events.values()).sort((a, b) => (a.id < b.id ? -1 : 1)),
    [events],
  );

  return {
    events: eventsInOrder,
    initialPage,
    isLoading: enabled && initialPage === undefined && !isError,
    isError,
    hasEarlier: cursor !== undefined,
    loadEarlier,
    isLoadingEarlier,
    retry: refetch,
  };
}
